import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  SwapHoriz,
  Person,
  Description,
  CheckCircle,
} from '@mui/icons-material';
import ClientSelect from '../selects/ClientSelect';
import ContractsActiveSelect from '../selects/ContractsActiveSelect';
import { Client } from '../../types';
import Swal from 'sweetalert2';

interface TransferServicoModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newContractId: number) => void;
  onCreateNewContract?: (clientId: number) => void;
  servicoTipo: string;
  servicoId: number;
  loading?: boolean;
}

const TransferServicoModal: React.FC<TransferServicoModalProps> = ({
  open,
  onClose,
  onConfirm,
  onCreateNewContract,
  servicoTipo,
  servicoId,
  loading = false,
}) => {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [createNewContract, setCreateNewContract] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Resetar estado quando modal abrir/fechar
  useEffect(() => {
    if (open) {
      setSelectedClient(null);
      setSelectedContractId(null);
      setCreateNewContract(false);
      setActiveStep(0);
      setError(null);
    }
  }, [open]);

  const handleClientChange = (clientId: number | null, client?: Client) => {
    setSelectedClient(client || null);
    setSelectedContractId(null); // Limpar contrato selecionado
    setError(null);
    
    if (clientId) {
      setActiveStep(1);
    } else {
      setActiveStep(0);
    }
  };

  const handleContractChange = (contractId: number | number[] | null) => {
    if (Array.isArray(contractId)) {
      setSelectedContractId(contractId.length > 0 ? contractId[0] : null);
    } else {
      setSelectedContractId(contractId);
    }
    setCreateNewContract(false);
    setError(null);
    
    if (contractId) {
      setActiveStep(2);
    } else {
      setActiveStep(1);
    }
  };

  const handleCreateNewContract = () => {
    setCreateNewContract(true);
    setSelectedContractId(null);
    setActiveStep(2);
  };

  const handleConfirm = () => {
    if (createNewContract) {
      if (!selectedClient) {
        setError('Selecione um cliente primeiro');
        return;
      }
      if (onCreateNewContract) {
        onCreateNewContract(selectedClient.id);
      }
      return;
    }

    if (!selectedContractId) {
      setError('Selecione um contrato para transferir o serviço');
      return;
    }
    Swal.fire({
      title: 'Confirmação',
      text: 'Tem certeza que deseja transferir o serviço para o contrato ' + selectedContractId + '?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim',
      cancelButtonText: 'Cancelar',
      customClass: {
        container: 'swal2-container-high-zindex'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        onConfirm(selectedContractId);
      }
    });
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const steps = [
    {
      label: 'Selecionar Cliente',
      description: 'Escolha o cliente que possui o contrato de destino',
      icon: <Person />,
    },
    {
      label: 'Selecionar Contrato',
      description: 'Escolha o contrato ativo para transferir o serviço',
      icon: <Description />,
    },
    {
      label: 'Confirmar Transferência',
      description: 'Revise os dados e confirme a transferência',
      icon: <CheckCircle />,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SwapHoriz color="primary" />
          <Typography variant="h6" component="div">
            Transferir Serviço
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Transferindo serviço <strong>{servicoTipo}</strong> (ID: {servicoId})
        </Typography>
        <Alert severity="warning"  sx={{borderRadius: '5px', border: '1px solid #ff9800', mt: 1 }}>
          Todos os Chamados e Equipamentos serão transferidos juntamente com o serviço
        </Alert>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: activeStep >= index ? 'primary.main' : 'grey.300',
                        color: activeStep >= index ? 'white' : 'grey.600',
                      }}
                    >
                      {step.icon}
                    </Box>
                  )}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {step.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                </StepLabel>
                <StepContent>
                  {index === 0 && (
                    <Box sx={{ mt: 2 }}>
                      <ClientSelect
                        value={selectedClient?.id || null}
                        onChange={handleClientChange}
                        label="Cliente de Destino"
                        placeholder="Digite para buscar um cliente"
                        required
                        helperText="Selecione o cliente que possui o contrato de destino"
                      />
                    </Box>
                  )}
                  
                  {index === 1 && selectedClient && (
                    <Box sx={{ mt: 2 }}>
                      <ContractsActiveSelect
                        value={selectedContractId}
                        onChange={handleContractChange}
                        label="Contrato de Destino"
                        placeholder="Selecione um contrato ativo"
                        required={!createNewContract}
                        clientId={selectedClient.id}
                        helperText="Selecione o contrato ativo para transferir o serviço"
                      />
                      {onCreateNewContract && (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleCreateNewContract}
                            disabled={createNewContract}
                            sx={{ mt: 1 }}
                          >
                            {createNewContract ? 'Novo Contrato Selecionado' : 'Criar Novo Contrato'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {index === 2 && selectedClient && (selectedContractId || createNewContract) && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Resumo da Transferência:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          • <strong>Serviço:</strong> {servicoTipo} (ID: {servicoId})
                        </Typography>
                        <Typography variant="body2">
                          • <strong>Cliente de Destino:</strong> {selectedClient.nome}
                        </Typography>
                        {createNewContract ? (
                          <Typography variant="body2">
                            • <strong>Contrato de Destino:</strong> Novo contrato será criado
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            • <strong>Contrato de Destino:</strong> ID {selectedContractId}
                          </Typography>
                        )}
                      </Alert>
                    </Box>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={(!selectedContractId && !createNewContract) || loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : (createNewContract ? <Description /> : <SwapHoriz />)}
        >
          {loading 
            ? (createNewContract ? 'Criando...' : 'Transferindo...') 
            : (createNewContract ? 'Criar Novo Contrato' : 'Transferir Serviço')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferServicoModal;
